import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useState, useContext, useMemo, useEffect } from 'react';
import uuid from 'uuid/v4';
import { AnnotationTypes, AnnotationMarkStates } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import { updateWindowSelectionAroundDraft } from '../draft/dom';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE, ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import { getRendererRangeInlineNodeNames } from '../../../actions/get-renderer-range-inline-node-names';
import { RendererContext as ActionsContext } from '../../RendererActionsContext';
import { useAnnotationManagerDispatch, useAnnotationManagerState } from '../contexts/AnnotationManagerContext';
import { useAnnotationRangeDispatch, useAnnotationRangeState } from '../contexts/AnnotationRangeContext';
export var SelectionInlineCommentMounter = /*#__PURE__*/React.memo(function (props) {
  var Component = props.component,
    range = props.range,
    draftRange = props.draftRange,
    isAnnotationAllowed = props.isAnnotationAllowed,
    wrapperDOM = props.wrapperDOM,
    onCloseProps = props.onClose,
    clearAnnotationDraft = props.clearAnnotationDraft,
    applyAnnotationDraftAt = props.applyAnnotationDraftAt,
    documentPosition = props.documentPosition,
    applyAnnotation = props.applyAnnotation,
    createAnalyticsEvent = props.createAnalyticsEvent,
    generateIndexMatch = props.generateIndexMatch;

  // if platform_renderer_annotation_draft_position_fix is enabled; then
  var _useAnnotationRangeDi = useAnnotationRangeDispatch(),
    promoteSelectionToDraft = _useAnnotationRangeDi.promoteSelectionToDraft,
    clearSelectionDraft = _useAnnotationRangeDi.clearSelectionDraft;
  var _useAnnotationRangeSt = useAnnotationRangeState(),
    selectionDraftDocumentPosition = _useAnnotationRangeSt.selectionDraftDocumentPosition;
  // else;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    draftDocumentPosition = _useState2[0],
    setDraftDocumentPosition = _useState2[1];
  // end-if

  var actions = useContext(ActionsContext);
  var _useAnnotationManager = useAnnotationManagerState(),
    isDrafting = _useAnnotationManager.isDrafting,
    draftId = _useAnnotationManager.draftId;
  var _useAnnotationManager2 = useAnnotationManagerDispatch(),
    annotationManager = _useAnnotationManager2.annotationManager,
    dispatch = _useAnnotationManager2.dispatch;
  var inlineNodeTypes = useMemo(function () {
    if (fg('annotations_defensive_node_name_calculations')) {
      if (!actions.isRangeAnnotatable(range)) {
        return undefined;
      }
      return getRendererRangeInlineNodeNames({
        pos: documentPosition,
        actions: actions
      });
    } else {
      if (!fg('editor_inline_comments_on_inline_nodes')) {
        return undefined;
      }
      if (actions.isValidAnnotationRange(range)) {
        return getRendererRangeInlineNodeNames({
          pos: documentPosition,
          actions: actions
        });
      } else {
        return undefined;
      }
    }
  }, [documentPosition, actions, range]);
  var onCreateCallback = useCallback(function (annotationId) {
    // We want to support creation on a documentPosition if the user is only using ranges
    // but we want to prioritize draft positions if they are being used by consumers
    var positionToAnnotate = fg('platform_renderer_annotation_draft_position_fix') ? selectionDraftDocumentPosition || documentPosition : draftDocumentPosition || documentPosition;
    if (!positionToAnnotate || !applyAnnotation) {
      // TODO: EDITOR-595 - This analytic event is temporary and should be removed once the following issue
      // has been identified and fixed: https://atlassian.slack.com/archives/C08JK0WSCH5/p1745902609966999
      if (createAnalyticsEvent && fg('platform_renderer_annotations_create_debug_logging')) {
        createAnalyticsEvent({
          action: 'failed',
          actionSubject: 'applyAnnotation',
          actionSubjectId: 'inlineCommentFailureReason',
          attributes: {
            reason: 'Annotation Position invalid',
            draftDocumentPosition: fg('platform_renderer_annotation_draft_position_fix') ? selectionDraftDocumentPosition : draftDocumentPosition,
            documentPosition: documentPosition,
            applyAnnotation: !!applyAnnotation,
            isDraftPositionFixEnabled: fg('platform_renderer_annotation_draft_position_fix')
          },
          eventType: EVENT_TYPE.OPERATIONAL
        }).fire(FabricChannel.editor);
      }
      return false;
    }
    if (fg('platform_renderer_annotations_create_debug_logging')) {
      actions._setDebugLogging(true);
    }

    // Evaluate position validity when the user commits the position to be annotated
    var isCreateAllowedOnPosition = actions.isValidAnnotationPosition(positionToAnnotate);
    if (!isCreateAllowedOnPosition) {
      return false;
    }
    var annotation = {
      annotationId: annotationId,
      annotationType: AnnotationTypes.INLINE_COMMENT
    };
    if (createAnalyticsEvent) {
      createAnalyticsEvent({
        action: ACTION.INSERTED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        attributes: {
          inlineNodeNames: inlineNodeTypes
        },
        eventType: EVENT_TYPE.TRACK
      }).fire(FabricChannel.editor);
    }
    if (fg('platform_renderer_annotations_create_debug_logging')) {
      var result = applyAnnotation(positionToAnnotate, annotation);
      actions._setDebugLogging(false);
      return result;
    } else {
      return applyAnnotation(positionToAnnotate, annotation);
    }
  }, [actions, documentPosition, applyAnnotation, draftDocumentPosition, createAnalyticsEvent, inlineNodeTypes, selectionDraftDocumentPosition]);
  var createIndexCallback = useCallback(function () {
    if (!documentPosition || !generateIndexMatch) {
      return false;
    }
    var result = generateIndexMatch(documentPosition);
    if (!result) {
      return false;
    }
    return result;
  }, [documentPosition, generateIndexMatch]);
  var applyDraftModeCallback = useCallback(function (options) {
    if (!documentPosition || !isAnnotationAllowed) {
      if (createAnalyticsEvent) {
        createAnalyticsEvent({
          action: ACTION.CREATE_NOT_ALLOWED,
          actionSubject: ACTION_SUBJECT.ANNOTATION,
          actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
          attributes: {
            inlineNodeNames: inlineNodeTypes
          },
          eventType: EVENT_TYPE.TRACK
        }).fire(FabricChannel.editor);
      }
      return false;
    }
    if (fg('platform_renderer_annotation_draft_position_fix')) {
      promoteSelectionToDraft(documentPosition);
    } else {
      setDraftDocumentPosition(documentPosition);
      applyAnnotationDraftAt && applyAnnotationDraftAt(documentPosition);
    }
    if (createAnalyticsEvent) {
      var uniqueAnnotationsInRange = range ? actions.getAnnotationsByPosition(range) : [];
      createAnalyticsEvent({
        action: ACTION.OPENED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        eventType: EVENT_TYPE.TRACK,
        attributes: {
          overlap: uniqueAnnotationsInRange.length,
          inlineNodeNames: inlineNodeTypes
        }
      }).fire(FabricChannel.editor);
    }
    window.requestAnimationFrame(function () {
      if (options.keepNativeSelection) {
        updateWindowSelectionAroundDraft(documentPosition);
      } else {
        var sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
        }
      }
    });
    var positionToAnnotate = fg('platform_renderer_annotation_draft_position_fix') ? selectionDraftDocumentPosition || documentPosition : draftDocumentPosition || documentPosition;
    if (!positionToAnnotate || !applyAnnotation || !options.annotationId) {
      return false;
    }
    var annotation = {
      annotationId: options.annotationId,
      annotationType: AnnotationTypes.INLINE_COMMENT
    };
    return applyAnnotation(positionToAnnotate, annotation);
  }, [documentPosition, isAnnotationAllowed, applyAnnotationDraftAt, createAnalyticsEvent, applyAnnotation, draftDocumentPosition, actions, range, inlineNodeTypes, promoteSelectionToDraft, selectionDraftDocumentPosition]);
  var removeDraftModeCallback = useCallback(function () {
    if (fg('platform_renderer_annotation_draft_position_fix')) {
      clearSelectionDraft();
    } else {
      clearAnnotationDraft && clearAnnotationDraft();
      setDraftDocumentPosition(null);
    }
    var sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
    }
  }, [clearAnnotationDraft, clearSelectionDraft]);
  var onCloseCallback = useCallback(function () {
    if (createAnalyticsEvent) {
      createAnalyticsEvent({
        action: ACTION.CLOSED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        eventType: EVENT_TYPE.TRACK,
        attributes: {
          inlineNodeNames: inlineNodeTypes
        }
      }).fire(FabricChannel.editor);
    }
    removeDraftModeCallback();
    onCloseProps();
  }, [onCloseProps, removeDraftModeCallback, createAnalyticsEvent, inlineNodeTypes]);
  useEffect(function () {
    if (fg('platform_editor_comments_api_manager')) {
      var allowAnnotation = function allowAnnotation() {
        if (isDrafting) {
          return false;
        }
        return isAnnotationAllowed;
      };
      annotationManager === null || annotationManager === void 0 || annotationManager.hook('allowAnnotation', allowAnnotation);
      return function () {
        annotationManager === null || annotationManager === void 0 || annotationManager.unhook('allowAnnotation', allowAnnotation);
      };
    }
  }, [annotationManager, isAnnotationAllowed, isDrafting]);
  useEffect(function () {
    if (fg('platform_editor_comments_api_manager')) {
      var startDraft = function startDraft() {
        var _result$inlineNodeTyp;
        if (isDrafting) {
          return {
            success: false,
            reason: 'draft-in-progress'
          };
        }
        if (!actions.isValidAnnotationRange(range)) {
          return {
            success: false,
            reason: 'invalid-range'
          };
        }
        var id = uuid();
        var result = applyDraftModeCallback({
          annotationId: id,
          keepNativeSelection: false
        });
        if (!result) {
          return {
            success: false,
            reason: 'invalid-range'
          };
        }
        dispatch({
          type: 'setDrafting',
          data: {
            isDrafting: true,
            draftId: id,
            draftActionResult: result
          }
        });
        dispatch({
          type: 'resetSelectedAnnotation'
        });
        return {
          success: true,
          // We cannot get a ref to the target element here
          // because the draft is not yet applied to the DOM
          targetElement: undefined,
          inlineNodeTypes: (_result$inlineNodeTyp = result.inlineNodeTypes) !== null && _result$inlineNodeTyp !== void 0 ? _result$inlineNodeTyp : [],
          actionResult: {
            step: result.step,
            doc: result.doc,
            inlineNodeTypes: result.inlineNodeTypes,
            targetNodeType: result.targetNodeType,
            originalSelection: result.originalSelection,
            numMatches: result.numMatches,
            matchIndex: result.matchIndex,
            pos: result.pos
          }
        };
      };
      annotationManager === null || annotationManager === void 0 || annotationManager.hook('startDraft', startDraft);
      return function () {
        annotationManager === null || annotationManager === void 0 || annotationManager.unhook('startDraft', startDraft);
      };
    }
  }, [annotationManager, isDrafting, applyDraftModeCallback, actions, range, dispatch]);
  useEffect(function () {
    if (fg('platform_editor_comments_api_manager')) {
      var clearDraft = function clearDraft() {
        if (!isDrafting) {
          return {
            success: false,
            reason: 'draft-not-started'
          };
        }
        dispatch({
          type: 'setDrafting',
          data: {
            isDrafting: false,
            draftId: undefined,
            draftActionResult: undefined
          }
        });
        onCloseCallback();
        return {
          success: true
        };
      };
      annotationManager === null || annotationManager === void 0 || annotationManager.hook('clearDraft', clearDraft);
      return function () {
        annotationManager === null || annotationManager === void 0 || annotationManager.unhook('clearDraft', clearDraft);
      };
    }
  }, [annotationManager, onCloseCallback, isDrafting, dispatch]);
  useEffect(function () {
    if (fg('platform_editor_comments_api_manager')) {
      var applyDraft = function applyDraft(id) {
        if (!isDrafting || !draftId) {
          return {
            success: false,
            reason: 'draft-not-started'
          };
        }
        var result = onCreateCallback(id);
        if (!result) {
          return {
            success: false,
            reason: 'range-no-longer-exists'
          };
        }
        onCloseCallback();
        dispatch({
          type: 'setDrafting',
          data: {
            isDrafting: false,
            draftId: undefined,
            draftActionResult: undefined
          }
        });
        dispatch({
          type: 'updateAnnotation',
          data: {
            id: id,
            selected: true,
            markState: AnnotationMarkStates.ACTIVE
          }
        });
        return {
          success: true,
          targetElement: undefined,
          actionResult: id !== draftId ? {
            step: result.step,
            doc: result.doc,
            inlineNodeTypes: result.inlineNodeTypes,
            targetNodeType: result.targetNodeType,
            originalSelection: result.originalSelection,
            numMatches: result.numMatches,
            matchIndex: result.matchIndex,
            pos: result.pos
          } : undefined
        };
      };
      annotationManager === null || annotationManager === void 0 || annotationManager.hook('applyDraft', applyDraft);
      return function () {
        annotationManager === null || annotationManager === void 0 || annotationManager.unhook('applyDraft', applyDraft);
      };
    }
  }, [annotationManager, onCreateCallback, onCloseCallback, isDrafting, draftId, dispatch]);

  // Please remove this NOP function when the flag platform_editor_comments_api_manager is removed.
  var nop = useMemo(function () {
    return function () {
      return false;
    };
  }, []);
  return /*#__PURE__*/React.createElement(Component, {
    range: range,
    draftRange: draftRange
    // Ignored via go/ees005
    // eslint-disable-next-line @atlaskit/editor/no-as-casting
    ,
    wrapperDOM: wrapperDOM.current,
    isAnnotationAllowed: isAnnotationAllowed,
    onClose: fg('platform_editor_comments_api_manager') ? nop : onCloseCallback,
    onCreate: fg('platform_editor_comments_api_manager') ? nop : onCreateCallback,
    getAnnotationIndexMatch: createIndexCallback,
    applyDraftMode: fg('platform_editor_comments_api_manager') ? nop : applyDraftModeCallback,
    removeDraftMode: fg('platform_editor_comments_api_manager') ? nop : removeDraftModeCallback,
    inlineNodeTypes: inlineNodeTypes
  });
});